import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useWalletStore } from '../stores/wallet';
import Icon from '../components/Icon';
import BraintreeDropin from '../components/BraintreeDropin';
import type { Transaction } from '@auction/shared';

// Braintree Sandbox Test Cards
const TEST_CARDS = [
  { name: 'Visa', number: '4111111111111111', icon: 'credit_card', color: 'bg-blue-500' },
  { name: 'Mastercard', number: '5555555555554444', icon: 'credit_card', color: 'bg-orange-500' },
  { name: 'Amex', number: '378282246310005', icon: 'credit_card', color: 'bg-green-500' },
  { name: 'Discover', number: '6011111111111117', icon: 'credit_card', color: 'bg-purple-500' },
  { name: 'JCB', number: '3530111333300000', icon: 'credit_card', color: 'bg-red-500' },
];

type PaymentMode = 'dropin' | 'quicktest';

export default function WalletPage() {
  const { wallet, loading, error, fetchWallet, depositWithCard, depositWithNonce, getTransactions, getClientToken } = useWalletStore();
  const [depositAmount, setDepositAmount] = useState('100');
  const [cardNumber, setCardNumber] = useState('4111111111111111');
  const [expirationMonth, setExpirationMonth] = useState('12');
  const [expirationYear, setExpirationYear] = useState('2026');
  const [cvv, setCvv] = useState('123');
  const [selectedCard, setSelectedCard] = useState('Visa');
  const [depositing, setDepositing] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);
  const [depositSuccess, setDepositSuccess] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('dropin');
  const [clientToken, setClientToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);

  const selectTestCard = (card: typeof TEST_CARDS[0]) => {
    setCardNumber(card.number);
    setSelectedCard(card.name);
    // Amex uses 4-digit CVV
    setCvv(card.name === 'Amex' ? '1234' : '123');
  };

  useEffect(() => {
    fetchWallet();
    getTransactions().then(setTransactions).catch(console.error);
  }, [fetchWallet, getTransactions]);

  // Load Braintree client token when using Drop-in mode
  useEffect(() => {
    if (paymentMode === 'dropin' && !clientToken && !loadingToken) {
      setLoadingToken(true);
      getClientToken()
        .then(setClientToken)
        .catch((err) => setDepositError(err.message))
        .finally(() => setLoadingToken(false));
    }
  }, [paymentMode, clientToken, loadingToken, getClientToken]);

  const handleQuickDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!depositAmount) return;

    setDepositing(true);
    setDepositError(null);
    setDepositSuccess(null);

    try {
      const amount = Math.round(parseFloat(depositAmount) * 100);
      const result = await depositWithCard(amount, cardNumber, expirationMonth, expirationYear, cvv);
      setDepositSuccess(result.message || 'Deposit successful!');
      // Refresh data
      fetchWallet();
      getTransactions().then(setTransactions);
    } catch (err) {
      setDepositError((err as Error).message);
    } finally {
      setDepositing(false);
    }
  };

  const handleDropinPayment = async (nonce: string, paymentType: string) => {
    setDepositing(true);
    setDepositError(null);
    setDepositSuccess(null);

    try {
      const amount = Math.round(parseFloat(depositAmount) * 100);
      const result = await depositWithNonce(amount, nonce);
      setDepositSuccess(`${paymentType} payment successful! ${result.message || ''}`);
      // Refresh data and get new client token for next payment
      fetchWallet();
      getTransactions().then(setTransactions);
      setClientToken(null); // Force refresh of client token
    } catch (err) {
      setDepositError((err as Error).message);
    } finally {
      setDepositing(false);
    }
  };

  const getTransactionIcon = (type: string): string => {
    switch (type) {
      case 'deposit': return 'savings';
      case 'withdrawal': return 'payments';
      case 'hold': return 'lock';
      case 'release': return 'lock_open';
      case 'capture': return 'credit_card';
      default: return 'receipt';
    }
  };

  const getTransactionIconColor = (type: string): string => {
    switch (type) {
      case 'deposit': return 'text-green-500';
      case 'withdrawal':
      case 'capture': return 'text-red-500';
      case 'hold': return 'text-orange-500';
      case 'release': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'deposit': return 'text-green-600';
      case 'withdrawal':
      case 'capture': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading && !wallet) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Icon name="account_balance_wallet" className="text-primary-600" />
        My Wallet
      </h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <Icon name="error" size="small" />
          {error}
        </div>
      )}

      {/* Balance Card */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Icon name="payments" size="small" className="text-gray-400" />
              Available Balance
            </p>
            <p className="text-4xl font-bold text-primary-600">
              £{wallet ? (wallet.balance / 100).toFixed(2) : '0.00'}
            </p>
          </div>
          <Icon name="account_balance_wallet" size="xlarge" className="text-primary-200" />
        </div>

        {wallet && wallet.totalHeld > 0 && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2">
            <Icon name="lock" size="small" className="text-orange-500" />
            <p className="text-sm text-gray-500">
              Held in active bids: <span className="font-semibold text-orange-600">£{(wallet.totalHeld / 100).toFixed(2)}</span>
            </p>
          </div>
        )}
      </div>

      {/* Deposit Form */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Icon name="account_balance" className="text-primary-600" />
          Add Funds
        </h2>

        {/* Payment Mode Tabs */}
        <div className="flex border-b mb-4">
          <button
            onClick={() => setPaymentMode('dropin')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
              paymentMode === 'dropin'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="payments" />
            PayPal & Cards
          </button>
          <button
            onClick={() => setPaymentMode('quicktest')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
              paymentMode === 'quicktest'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="speed" />
            Quick Test Cards
          </button>
        </div>

        {/* Amount Selection (shared) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
          <div className="flex gap-2 mb-2">
            {[10, 50, 100, 500].map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => setDepositAmount(String(amt))}
                className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                  depositAmount === String(amt)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                £{amt}
              </button>
            ))}
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
            <input
              type="number"
              step="0.01"
              min="1"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              className="input pl-7"
              placeholder="100.00"
            />
          </div>
        </div>

        {/* Braintree Drop-in UI */}
        {paymentMode === 'dropin' && (
          <div className="mt-4">
            {loadingToken ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-500">Loading payment options...</span>
              </div>
            ) : clientToken ? (
              <BraintreeDropin
                clientToken={clientToken}
                amount={Math.round(parseFloat(depositAmount || '0') * 100)}
                onPaymentSuccess={handleDropinPayment}
                onError={(err) => setDepositError(err)}
              />
            ) : (
              <div className="text-center py-4 text-gray-500">
                <Icon name="error_outline" className="text-2xl mb-2" />
                <p>Failed to load payment form. Please refresh.</p>
              </div>
            )}
          </div>
        )}

        {/* Quick Test Card Form */}
        {paymentMode === 'quicktest' && (
          <>
            {/* Test Card Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Select Test Card
              </label>
              <div className="flex flex-wrap gap-2">
                {TEST_CARDS.map((card) => (
                  <button
                    key={card.name}
                    type="button"
                    onClick={() => selectTestCard(card)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                      selectedCard === card.name
                        ? `${card.color} text-white shadow-md scale-105`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon name={card.icon} className="text-lg" />
                    {card.name}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleQuickDeposit} className="space-y-4">
              {/* Card Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Number
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    ({selectedCard} test card)
                  </span>
                </label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  className="input"
                  placeholder="4111111111111111"
                  maxLength={19}
                />
              </div>

              {/* Expiration + CVV Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <input
                    type="text"
                    value={expirationMonth}
                    onChange={(e) => setExpirationMonth(e.target.value)}
                    className="input"
                    placeholder="12"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="text"
                    value={expirationYear}
                    onChange={(e) => setExpirationYear(e.target.value)}
                    className="input"
                    placeholder="2026"
                    maxLength={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                  <input
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value)}
                    className="input"
                    placeholder="123"
                    maxLength={4}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={depositing}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {depositing ? (
                  <>
                    <span className="animate-spin">
                      <Icon name="hourglass_empty" />
                    </span>
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon name="bolt" />
                    Quick Deposit £{depositAmount || '0'}
                  </>
                )}
              </button>
            </form>
          </>
        )}

        {/* Status Messages */}
        {depositError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <Icon name="error" />
            {depositError}
          </div>
        )}
        {depositSuccess && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <Icon name="check_circle" />
            {depositSuccess}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 flex items-center gap-2">
            <Icon name="info" />
            <strong>Braintree Sandbox</strong> - Real API, test credentials
          </p>
          <p className="text-xs text-blue-600 mt-1">
            {paymentMode === 'dropin' 
              ? 'Use PayPal sandbox or test cards. For PayPal, log in with sandbox buyer account.'
              : 'Test cards: 4111111111111111 (Visa), 5555555555554444 (MC)'}
          </p>
        </div>
      </div>

      {/* Transaction History */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Transaction History</h2>
        {transactions.length > 0 ? (
          <div className="divide-y">
            {transactions.map((tx) => (
              <div key={tx.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name={getTransactionIcon(tx.type)} className={`text-2xl ${getTransactionIconColor(tx.type)}`} />
                  <div>
                    <p className="font-medium capitalize">{tx.type}</p>
                    <p className="text-sm text-gray-500">{tx.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-medium ${getTransactionColor(tx.type)}`}>
                    {tx.type === 'deposit' || tx.type === 'release' ? '+' : '-'}
                    £{(tx.amount / 100).toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {format(new Date(tx.timestamp), 'MMM d, HH:mm')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No transactions yet.</p>
        )}
      </div>
    </div>
  );
}
